<!--
  - Copyright (c) 2021. Trade Research Advisory (Pty) Ltd, P.O.Box 336, Cornwall Hill, Irene, 0178
  - #  This file forms part of the Trade-DSM application frontend.
  - #  https://github.com/Tooblippe/trade-dsm-frontend
  -->

<template>
  <div
    id="page"
    v-if="show_app"
    :class="{'institution-dark': stylef['dark_mode'] === true}"
  >
    <v-alert v-if="institution_load_error" type="error" tile>
      {{ institution_load_error }}
    </v-alert>
    <div v-else v-html="sanitized_header"></div>
    <v-container v-if="!institution_load_error" fluid class="pa-0">
      <v-alert v-if="product_load_error" type="error" tile>
        {{ product_load_error }}
      </v-alert>
      <v-row no-gutters>
        <v-col>
          <div :style="[get_app_pos]">
            <div
              :style="[entry_box_style]"
              class="mb-2"
              v-html="sanitized_disclaimer"
            ></div>
          </div>
        </v-col>
      </v-row>
      <div :style="get_app_pos">
        <v-row no-gutters>
          <!--  product box-->

          <v-col cols="12" xs="12" sm="12" md="6" lg="3">
            <div
              id="box_1"
              :style="[
                entry_box_style,
                box_1_active ? stylef['box_focus'] : stylef['box_unfocus'],
              ]"
              class="fill-height mt-lg-2 mr-lg-3 mb-lg-1 mr-md-2 mt-md-5"
            >
              <span :style="stylef['circle']">1</span
              ><span :style="stylef['tagline']">Your product of interest</span>
              <div v-for="text in product_box.text" :key="text" class="mt-1">
                {{ text }}
              </div>

              <v-radio-group
                row
                v-model="selectedGuidanceOption"
                class="ml-2"
                :disabled="
                  box_2_active ||
                  box_3_active ||
                  box_3_next_clicked ||
                  combinations_loading
                "
              >
                <v-radio
                  label="I know which product or the tariff code"
                  :value="true"
                  class="boxtext"
                  :color="main_color"
                  @change="clear_box_1"
                ></v-radio>
                <v-radio
                  label="Guide me to find the product or tariff code"
                  :value="false"
                  :color="main_color"
                  @change="clear_box_1"
                ></v-radio>
              </v-radio-group>
              <!--              -->
              <div id="hs6_only" v-if="selectedGuidanceOption">
                <v-autocomplete
                  :items="all_hs6_items_list"
                  @change="set_hs6_selection($event)"
                  label="Select your specific product (HS6 tariff code)"
                  v-bind="drop_down_props"
                  :style="drop_down_style"
                  :color="main_color"
                  :value="hs6_selected"
                  :no-data-text="hs6_loading ? 'Data loading....' : 'No data available'"
                  :disabled="
                    box_2_active ||
                    box_3_active ||
                    box_3_next_clicked ||
                    combinations_loading
                  "
                ></v-autocomplete>
              </div>

              <div id="group_to_hs6" v-if="!selectedGuidanceOption">
                <v-progress-circular
                  v-if="chapters_loading"
                  indeterminate
                  :size="25"
                  :color="main_color"
                  class="ml-2 mt-2"
                ></v-progress-circular>
                <v-autocomplete
                  v-else
                  :items="chapter_items_list"
                  @change="load_hs2_codes($event)"
                  label="Please tell us more about your product"
                  v-bind="drop_down_props"
                  :style="drop_down_style"
                  no-data-text="Loading..."
                  :color="main_color"
                  :value="chapter_selected"
                  :disabled="
                    box_2_active ||
                    box_3_active ||
                    box_3_next_clicked ||
                    combinations_loading
                  "
                ></v-autocomplete>

                <v-progress-circular
                  v-if="hs2_loading"
                  indeterminate
                  :size="25"
                  :color="main_color"
                  class="ml-2 mt-2"
                ></v-progress-circular>
                <v-autocomplete
                  v-else-if="hs2_items_list.length > 0"
                  :items="hs2_items_list"
                  @change="load_hs4_codes($event)"
                  label="Please be more specific"
                  v-bind="drop_down_props"
                  :style="drop_down_style"
                  :color="main_color"
                  :value="hs2_selected"
                  :disabled="
                    box_2_active ||
                    box_3_active ||
                    box_3_next_clicked ||
                    combinations_loading
                  "
                ></v-autocomplete>

                <v-progress-circular
                  v-if="hs4_loading"
                  indeterminate
                  :size="25"
                  :color="main_color"
                  class="ml-2 mt-2"
                ></v-progress-circular>
                <v-autocomplete
                  v-if="hs4_items_list.length > 0"
                  :items="hs4_items_list"
                  @change="load_hs6_codes($event)"
                  label="We are getting close, drill down even further"
                  v-bind="drop_down_props"
                  :style="drop_down_style"
                  :color="main_color"
                  :value="hs4_selected"
                  :disabled="
                    box_2_active ||
                    box_3_active ||
                    box_3_next_clicked ||
                    combinations_loading
                  "
                ></v-autocomplete>

                <v-progress-circular
                  v-if="hs6_loading"
                  indeterminate
                  :size="25"
                  :color="main_color"
                  class="ml-2 mt-2"
                ></v-progress-circular>

                <v-autocomplete
                  v-show="hs6_items_list.length > 0"
                  :items="hs6_items_list"
                  @change="set_hs6_selection($event)"
                  label="Now please select a specific product"
                  v-bind="drop_down_props"
                  :style="drop_down_style"
                  :color="main_color"
                  :value="hs6_selected"
                  :disabled="
                    box_2_active ||
                    box_3_active ||
                    box_3_next_clicked ||
                    combinations_loading
                  "
                ></v-autocomplete>
              </div>
            </div>
          </v-col>

          <!--  result boc-->
          <v-col cols="12" xs="12" sm="12" md="6" lg="3">
            <div
              :style="[entry_box_style]"
              v-if="query_result"
              class="fill-height mt-lg-2 mt-sm-5 mr-sm-2 mt-6"
            >
              <span :style="stylef['circle_info']">Info</span
              ><span :style="stylef['tagline_info']"
                >{{ hs6_selected }} Export potential</span
              >
              <template v-if="query_result['result']['market_count'] > 0">
                <div class="ma-3">
                  {{ result_map_box_text }}
                </div>

                <div>
                  <div id="product_map">
                    <v-row align="center" no-gutters>
                      <v-col cols="12" sm="2">
                        <div class="mt-1">
                          <img
                            class="ml-2 ma-0 pa-0"
                            alt="Home Market"
                            :src="institution['flag_image_b64']"
                            width="50px"
                          />
                        </div>
                      </v-col>
                      <v-col cols="12" sm="10">
                        <div class="font-weight-bold text-left ml-sm-2">
                          <span>Home market:</span>
                          <span class="font-italic"> {{ institution['C_Name'] }}</span>
                        </div>
                      </v-col>
                    </v-row>
                    <div
                      class="chartWithOverlay"
                      v-if="query_result['result']['chart_data']"
                    >
                      <Plotly
                        :data="generate_chart_html.data"
                        :layout="generate_chart_html.layout"
                        :display-mode-bar="false"
                        class="mb-2"
                      ></Plotly>
                      <div class="caption">
                        <div>
                          <span class="home_dot"></span>Home market -
                          {{ institution['C_Name'] }} [{{ institution['model_name'] }}]
                        </div>
                        <div>
                          <span class="green_dot"></span>Markets importing selected
                          product
                        </div>
                        <div>{{ institution['map_footer_explanation'] }}</div>
                        <div>{{ institution['map_footer_disclaimer'] }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div v-if="combinations_loading">
                  <span>Loading ...</span>
                  <v-progress-circular
                    indeterminate
                    :size="25"
                    :color="main_color"
                    class="ml-2 mt-2"
                  ></v-progress-circular>
                </div>
                <v-alert v-else-if="combination_error" type="info" dense text>
                  {{ combination_error }}
                </v-alert>
              </template>
            </div>
          </v-col>

          <!--  market box-->
          <v-col cols="12" xs="12" sm="12" md="6" lg="3">
            <div
              :style="[
                entry_box_style,
                box_2_active ? stylef['box_focus'] : stylef['box_unfocus'],
              ]"
              class="fill-height ml-lg-2 mt-lg-2 mb-lg-0 ml-md-0 mt-md-8 mr-md-2 mt-sm-7 mr-sm-2 mb-sm-12 mt-8"
            >
              <span :style="stylef['circle']">2</span
              ><span :style="stylef['tagline']">Your existing trading markets</span>
              <template v-if="query_result['result']['market_count'] > 0">
                <div class="pa-2">
                  <div v-if="hs2_selected || hs4_selected">
                    Your selected product:<br /><br />
                    <span class="green_dot"></span>
                    <span class="font-italic text--accent-1"
                      >Chapter: {{ hs2_selected }}-{{
                        this.hs2_codes[hs2_selected]
                      }}</span
                    ><br /><br />
                    <span class="green_dot"></span>
                    <span class="ont-italic text--accent-1"
                      >Sub-Heading: {{ hs4_selected }}-{{
                        this.hs4_codes[hs4_selected]
                      }}</span
                    ><br /><br />
                  </div>

                  <span class="green_dot"></span>
                  <span class="font-weight-bold font-italic text--accent-1"
                    >Tariff Code: {{ hs6_selected }}-{{
                      this.all_hs6_codes[hs6_selected]
                    }}</span
                  >. <br /><br />
                  Have you exported this product or any other products in the last three
                  years?
                </div>
                <v-radio-group
                  row
                  v-model="export_already_radio"
                  class="ml-2"
                  :disabled="box_2_next_clicked"
                >
                  <v-radio
                    label="Yes"
                    value="1"
                    class="boxtext"
                    :color="main_color"
                  ></v-radio>
                  <v-radio
                    label="No"
                    value="0"
                    :color="main_color"
                    @click="selected_current_markets = []"
                  ></v-radio>
                </v-radio-group>

                <div v-if="export_already_radio === '1'">
                  <div class="pa-2">Please indicate to which country(ies)?</div>
                  <v-autocomplete
                    :items="query_result['result']['all_markets']"
                    v-model="selected_current_markets"
                    v-bind="drop_down_props"
                    multiple
                    :style="drop_down_style"
                    chips
                    small-chips
                    class="pa-1"
                    label="Select existing trading countries"
                    :color="main_color"
                    :disabled="box_2_next_clicked"
                  ></v-autocomplete>
                </div>
                <div class="text-right mt-2">
                  <v-btn
                    v-if="box_2_active"
                    :style="stylef['button']"
                    @click="box_2_previous_button"
                    class="mr-2"
                  >
                    <v-icon>mdi-arrow-left</v-icon>
                    Previous
                  </v-btn>
                  <v-btn
                    :disabled="box_2_next_enabled"
                    :style="stylef['button']"
                    @click="previous_clicked"
                    >Next
                    <v-icon>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-else>
                <div v-if="combinations_loading">
                  <span>Loading ...</span>
                  <v-progress-circular
                    indeterminate
                    :size="25"
                    :color="main_color"
                    class="ml-2 mt-2"
                  ></v-progress-circular>
                </div>
              </template>
            </div>
            <!--            </div>-->
          </v-col>

          <!--  spesific market -->
          <v-col cols="12" xs="12" sm="12" md="6" lg="3">
            <div
              :style="[
                entry_box_style,
                box_3_active ? stylef['box_focus'] : stylef['box_unfocus'],
              ]"
              class="fill-height ml-lg-2 mt-lg-2 mb-lg-0 mt-md-8 mr-md-2 ml-md-1 mt-sm-10 mr-sm-2 mb-sm-12 mt-10"
            >
              <span :style="stylef['circle']">3</span
              ><span :style="stylef['tagline']">Narrow your market focus</span>

              <template v-if="box_2_next_clicked">
                <div class="mt-1 ma-2 mb-4">
                  Select a target market that you are interested in for your currently
                  selected product.
                </div>
                <div>
                  <v-autocomplete
                    :items="market_results_dropdown_data"
                    v-model="spesific_selected_market"
                    @change="box_3_clicked($event)"
                    v-bind="drop_down_props"
                    :style="drop_down_style"
                    label="Select market"
                    class="boxtext"
                    :color="main_color"
                    :disabled="box_3_next_clicked"
                  ></v-autocomplete>
                </div>
                <div class="text-right mt-2">
                  <v-btn
                    v-if="box_2_next_clicked && !box_3_next_clicked"
                    :style="stylef['button']"
                    @click="box_3_previous_button"
                    class="mr-2"
                  >
                    <v-icon>mdi-arrow-left</v-icon>
                    Previous
                  </v-btn>
                </div>
              </template>
            </div>
          </v-col>
        </v-row>
        <v-row no-gutters class="mt-0">
          <v-col cols="12" xs="12" sm="12" lg="12">
            <div
              id="contact_form"
              :style="[
                entry_box_style,
                box_3_next_clicked ? stylef['box_focus'] : stylef['box_unfocus'],
              ]"
              v-if="query_result"
              class="ml-1 mr-1 mt-12 mb-2 ml-md-0 mr-md-2 mt-sm-8 mr-sm-2 mr-0 mt-sm-12 mt-lg-5"
            >
              <span class="ml-4" :style="stylef['circle']">4</span
              ><span :style="stylef['tagline']"
                >In order for us to send you the report, please provide your contact
                information and click submit.</span
              >
              <v-form ref="user_input_form" class="ma-5" :disabled="!box_3_next_clicked">
                <!--              <v-form ref="user_input_form" class="ma-5">-->
                <v-row no-gutters>
                  <v-col cols="12" lg="6" md="6" sm="12">
                    <div class="mr-5">
                      <v-text-field
                        dense
                        v-model="user_data.firstname"
                        label="First name"
                        :rules="required_rule"
                        class="mb-4"
                        :color="main_color"
                        :error="firstname_rules"
                      ></v-text-field>
                      <v-text-field
                        dense
                        v-model="user_data.lastname"
                        label="Last name"
                        :rules="required_rule"
                        class="mb-4"
                        :color="main_color"
                        :error="lastname_rules"
                      ></v-text-field>
                      <v-text-field
                        dense
                        v-model="user_data.company"
                        label="Company name"
                        :rules="required_rule"
                        class="mb-5"
                        :color="main_color"
                        :error="company_name_rules"
                      ></v-text-field>
                      <!--                      <v-text-field-->
                      <!--                        dense-->
                      <!--                        v-model="user_data.website"-->
                      <!--                        label="Website"-->
                      <!--                        :rules="website_rule"-->
                      <!--                        class="mb-4"-->
                      <!--                        :color="main_color"-->
                      <!--                        :error="website_rules"-->
                      <!--                      ></v-text-field>-->
                      <!--                      <v-text-field-->
                      <!--                        dense-->
                      <!--                        v-model="user_data.location"-->
                      <!--                        label="Location"-->
                      <!--                        :rules="required_rule"-->
                      <!--                        class="mb-4"-->
                      <!--                        :color="main_color"-->
                      <!--                        :error="location_rules"-->
                      <!--                      ></v-text-field>-->
                      <!--                      <v-text-field-->
                      <!--                        dense-->
                      <!--                        v-model="user_data.city"-->
                      <!--                        label="City/Town/Province"-->
                      <!--                        :rules="required_rule"-->
                      <!--                        :color="main_color"-->
                      <!--                        :error="city_rules"-->
                      <!--                      ></v-text-field>-->
                      <v-text-field
                        dense
                        v-model="hs6_selected"
                        :value="hs6_selected"
                        :disabled="true"
                        label="Product code selected"
                        :rules="required_rule"
                        class="mb-4 mt-8"
                        :color="main_color"
                        :error="hs6_selected.length === 0"
                      ></v-text-field>
                      <v-text-field
                        dense
                        v-model="spesific_selected_market"
                        :value="spesific_selected_market"
                        :disabled="true"
                        label="Market preference selected"
                        :rules="required_rule"
                        class="mb-1"
                        :color="main_color"
                        :error="spesific_selected_market.length === 0"
                      ></v-text-field>
                    </div>
                    <div class="pr-4">
                      <span> Additional information:</span>
                      <v-textarea
                        v-model="customer_note"
                        label="Optional - max 500 characters"
                        :color="main_color"
                        clearable
                        counter
                        :rules="text_rules"
                        background-color="grey lighten-4"
                      ></v-textarea>
                    </div>
                  </v-col>
                  <v-col cols="12" lg="6" sm="12">
                    <div>
                      <v-text-field
                        dense
                        v-model="user_data.email"
                        label="Email address"
                        :rules="email_rule"
                        @change="user_data.email = user_data.email.toLowerCase()"
                        @input="user_data.email = user_data.email.toLowerCase()"
                        class="mb-4"
                        :color="main_color"
                        :error="email_rules"
                      ></v-text-field>
                      <v-text-field
                        dense
                        v-model="user_data.phone"
                        label="Phone"
                        :rules="phonerequired_rule"
                        class="mb-4"
                        :color="main_color"
                        :error="phone_rules"
                      ></v-text-field>
                      <!--                      <v-text-field-->
                      <!--                        dense-->
                      <!--                        v-model="user_data.mobile_phone"-->
                      <!--                        label="Mobile"-->
                      <!--                        class="mb-4"-->
                      <!--                        :rules="phonerequired_rule"-->
                      <!--                        :color="main_color"-->
                      <!--                        :error="mobilephone_rules"-->
                      <!--                      ></v-text-field>-->
                      <!--                      <v-text-field-->
                      <!--                        dense-->
                      <!--                        v-model="user_data.country_state"-->
                      <!--                        label="State"-->
                      <!--                        :rules="state_required_rule"-->
                      <!--                        class="mb-4"-->
                      <!--                        :color="main_color"-->
                      <!--                        :error="state_rules"-->
                      <!--                      ></v-text-field>-->
                      <v-text-field
                        dense
                        v-model="user_data.zipcode"
                        label="Zip / Postal code"
                        :rules="zip_required_rule"
                        class="mb-4"
                        :color="main_color"
                        :error="zip_rules"
                      ></v-text-field>

                      <v-checkbox
                        :label="institution['consent_data_text']"
                        v-model="user_data.required_1"
                        :rules="data_rule"
                        class="mb-4"
                        :color="main_color"
                        messages="Required"
                        :error="!user_data.required_1"
                      ></v-checkbox>
                      <p>
                        <a :href="institution.website_url" target="_blank"
                          >View institution website</a
                        >
                      </p>
                      <v-checkbox
                        :label="institution['contact_contact_text']"
                        v-model="user_data.optional_1"
                        class="mb-4"
                        :color="main_color"
                        messages="Optional"
                      ></v-checkbox>
                      <v-checkbox
                        :label="
                          institution.appointment_label ||
                          'I would like to make an appointment to discuss exporting'
                        "
                        v-model="user_data.optional_2"
                        class="mb-4"
                        :color="main_color"
                        messages="Optional"
                      ></v-checkbox>

                      <span class="red--text font-weight-bold" v-if="input_form_validated"
                        >Correct missing information to enable submit.</span
                      >
                    </div>
                  </v-col>
                  <v-col sm="12" class="text-center">
                    <v-btn
                      v-if="box_3_next_clicked"
                      :style="stylef['button']"
                      @click="form_previous_clicked"
                      class="mr-2 mt-1"
                      x-large
                      width="190px"
                    >
                      <v-icon>mdi-arrow-left</v-icon>
                      Previous
                    </v-btn>
                    <v-btn
                      :disabled="!rules_valid"
                      :style="stylef['button']"
                      class="mr-2 mt-1"
                      @click="start_over_clicked"
                      x-large
                      width="190px"
                      >Start Over
                      <v-icon class="">mdi-undo</v-icon>
                    </v-btn>
                    <v-btn
                      :style="stylef['button']"
                      class="mt-1 mr-2"
                      @click="reset_form_data"
                      x-large
                      width="190px"
                      >Clear form
                      <v-icon class="ml-1">mdi-delete</v-icon>
                    </v-btn>

                    <v-btn
                      :disabled="input_form_validated"
                      @click="submit_request"
                      :style="stylef['button']"
                      class="green mt-1 mr-2"
                      x-large
                      width="190px"
                      >Submit
                      <v-icon class="ml-1">mdi-cube-send</v-icon>
                    </v-btn>
                    <div class="caption text-right mt-1 mr-2" data-testid="build-time">
                      {{ build_time }}
                    </div>
                  </v-col>
                  <!--                  <v-col>-->
                  <!--                    <v-btn-->
                  <!--                      text-->
                  <!--                      x-small-->
                  <!--                      @click="temp_auto_fill_and_submit"-->
                  <!--                      class="text-right"-->
                  <!--                      >A-->
                  <!--                    </v-btn>-->
                  <!--                  </v-col>-->
                </v-row>
              </v-form>
            </div>
          </v-col>
        </v-row>
      </div>
      <app_dialog
        :show_dialog="show_success_page"
        :stylef="stylef"
        :brand_display_name="institution.brand_display_name"
        :website_url="institution.website_url"
        :maximum_reports="institution.maximum_number_of_reports"
        :appointment_label="institution.appointment_label"
        :appointment_booking_url="
          appointment_requested ? institution.appointment_booking_url : null
        "
        @ANOTHER_REPORT="start_over_clicked"
      ></app_dialog>
      <Loader :loading="loading" :stylef="stylef"></Loader>
    </v-container>
    <div v-if="!institution_load_error" v-html="sanitized_footer"></div>
  </div>
</template>

<script>
  import axios from 'axios';
  import log from '@/components/crm-lead-generator/helpers/logging';
  import get_query_params from '@/components/crm-lead-generator/helpers/query_params';
  // import load_institution_template from '@/components/crm-lead-generator/templates/template_loader';
  // import {GChart} from 'vue-google-charts';
  import chart_options from '@/components/crm-lead-generator/templates/chart_options';
  import html2canvas from 'html2canvas';
  import _ from 'lodash';
  import DOMPurify from 'dompurify';
  import app_dialog from '@/components/crm-lead-generator/components/app_dialog.vue';
  import Loader from '@/components/crm-lead-generator/components/loader.vue';

  import {Plotly} from 'vue-plotly';

  const FIRSTNAME_LENGTH = 3;
  const LASTNAME_LENGTH = 3;
  const COMPANY_NAME_LENGTH = 3;
  const LOCATION_LENGTH = 3;
  const CITY_LENGTH = 3;
  const PHONE_LENGTH = 9;
  const MOBILE_PHONE_LENGTH = 9;
  const STATE_LENGTH = 4;
  const ZIP_LENGTH = 4;

  export default {
    name: 'crm-lead-generator',
    components: {
      Loader,
      app_dialog,
      // GChart,
      Plotly,
    },
    async beforeMount() {
      this.emf_lead_gen_url = process.env.VUE_APP_URL;
      this.backup_query_result = _.clone(this.query_result);
      this.backup_user_data = _.clone(this.user_data);

      const query_params = get_query_params();
      this.institution_uuid = query_params.get('inst');
      if (!this.institution_uuid) {
        try {
          const response = await axios.get(this.emf_lead_gen_url + '/v2/post-bootstrap');
          this.institution_uuid = response.data.institution_uuid;
        } catch (_) {
          this.institution_load_error = 'This link does not contain an institution key.';
          return;
        }
      }
      await this.load_institution_data();
      if (this.institution_load_error) return;
      await Promise.all([
        this.load_all_hs6_codes(),
        this.load_chapter_data(),
        this.map_hs2_to_chapter_codes(),
      ]);
    },
    mounted() {
      this.popstate_handler = () => {
        const currentUuid = new URLSearchParams(window.location.search).get('inst');
        if (currentUuid !== this.institution_uuid) window.location.reload();
      };
      window.addEventListener('popstate', this.popstate_handler);
    },
    beforeDestroy() {
      window.removeEventListener('popstate', this.popstate_handler);
    },
    data() {
      return {
        show_app: true,
        institution_uuid: null,
        institution_load_error: '',
        selectedGuidanceOption: true,
        knowTheCode: true,
        helpFindCode: false,
        institution: {},
        text_rules: [v => !v || v.length <= 500 || 'Max 500 characters'],
        popstate_handler: null,
        product_load_error: '',
        combination_error: '',
        customer_note: '',
        combinations_loading: false,
        box_2_next_clicked: false,
        box_3_next_clicked: false,
        loading: false,
        show_success_page: false,
        appointment_requested: false,
        build_time: process.env.VUE_APP_BUILD_TIME || 'development',
        emf_lead_gen_url: '',
        chapters: {},
        hs2_codes: {},
        hs4_codes: {},
        hs6_codes: {},
        all_hs6_codes: {},
        hs2_to_chapter_map: {},
        chapter_selected: '',
        hs2_selected: '',
        hs4_selected: '',
        hs6_selected: '',
        //
        chapters_loading: true,
        hs2_loading: false,
        hs4_loading: false,
        hs6_loading: false,
        //
        spesific_selected_market: '',
        selected_current_markets: [],
        //
        products_map_b64: '-',
        top6_map_b64: '-',
        //
        export_already_radio: null,
        top6_result: {
          result: {
            chart_data: [],
          },
        },
        //
        chart_options: chart_options,

        dummy_chartOptions: {
          chart: {
            title: 'Export Potential',
            subtitle: '',
          },
        },
        insitution_templates: {
          HEADER: '', //html
          FOOTER: '', //html
          STYLES: {
            style: {
              application_position: {
                margin: 'auto',
                width: '90%',
              },
              box_focus: {
                boxShadow: '0 0 20px #F59F26',
                outline: '3px solid #F59F26 !important',
              },
              box_unfocus: {},
              button: {
                backgroundColor: '#000033',
                color: 'white',
              },
              checkbox: {
                color: '#000033',
              },
              circle: {
                MozBorderRadius: '50%',
                WebkitBorderRadius: '50%',
                backgroundColor: '#000033',
                borderRadius: '50%',
                color: '#fffefe',
                display: 'inline-block',
                fontWeight: 'bold',
                lineHeight: '40px',
                marginBottom: '10px',
                marginRight: '5px',
                textAlign: 'center',
                width: '40px',
              },
              circle_info: {
                MozBorderRadius: '50%',
                WebkitBorderRadius: '50%',
                backgroundColor: '#000033',
                borderRadius: '50%',
                color: '#fffefe',
                display: 'inline-block',
                fontWeight: 'bold',
                lineHeight: '40px',
                marginBottom: '10px',
                marginRight: '5px',
                textAlign: 'center',
                width: '40px',
              },
              drop_down: {
                backgroundColor: '#FFFFFF',
                borderRadius: '5px',
                marginTop: '15px',
              },
              drop_down_props: {
                dense: 'dense',
                flat: 'flat',
                'hide-details': 'hide-details',
                outlined: 'outlined',
              },
              entry_box: {
                backgroundColor: 'white',
                borderRadius: '7px',
                color: '#000033',
                fontFamily: "'Barlow',sans-serif",
                fontSize: '0.95rem',
                fontWeight: 500,
                letterSpacing: '1px',
                lineHeight: '1.6',
                marginLeft: '3px',
                marginTop: '10px',
                outline: '1px solid #000033',
                padding: '15px',
                textAlign: 'justify',
              },
              header: {},
              main_color: '#000033',
              main_color_and_text: {
                backgroundColor: '#000033',
                color: 'white',
              },
              page_font: {
                fontFamily: "'Barlow',sans-serif",
              },
              tagline: {
                color: '#000033',
                fontSize: 'larger',
                fontWeight: 'bold',
                margin: '2px',
                padding: '5px',
              },
              tagline_info: {
                color: '#000033',
                fontSize: 'larger',
                fontWeight: 'bold',
                margin: '2px',
                padding: '5px',
              },
            },
          },
        },

        //
        referer_data: {
          request_data: {}, // will hold the request data as parsed from the incoming url and parameters
          institution_data: {}, // will hold all the settings for this insitution e.g. exchange rate, html headers etc
          products_data: [], // will hold a listing of products, descriptions
        },
        //
        dummy_data: {
          markets: ['a', 'b'],
          dummy_user: {
            firstname: 'Name',
            lastname: 'Surame',
            company: 'Company',
            website: 'www.company.co.za',
            city: 'City',
            location: 'Province',
            product_code: 'My Code',
            market_preference: 'Angola',
            email: 'email@domain.com',
            phone: '123456789',
            mobile_phone: '123456789',
            country_state: 'state',
            zipcode: '11111',
            //
            data_permission: true,
            required_1: true,
            //
            contact_permission: true,
            optional_1: true,
            //
            appointment_required: false,
            optional_2: false,
          },
          dummy_request_data: {}, // incomeing from request
          dummy_institution_data: {
            html: {
              header: '', //html
              footer: '', //html
              css: '',
            },
          }, // response from server to be set
          dummy_products_data: ['HS100310', 'HS010121', 'HS010121'],
          dummy_product_box_text: [
            'Would you like to have a report with potential realistic export markets for your product? ' +
              'Please indicate the product (tariff code) you are interested in. ' +
              'You will need to answer further questions before a link to the report will be emailed to you. ',
          ],
        },
        logging_on: true,
        product_box: {
          text: [],
          completed: true,
          value: null,
        },
        market_box: {
          text: [
            'Box2 Would you like to have a report with potential realistic export markets for your product?',
            'You will need to answer a few questions before a link to the report will be emailed to you',
            'Please indicate product (tariff code) you are interested in exporting.',
          ],
          completed: true,
          value: [],
        },
        result_map_box: {
          text: ['Used?'],
          completed: true,
        },
        refined_market_box: {
          text: [
            'Spesific Markget Would you like to have a report with potential realistic export markets for your product?',
            'You will need to answer a few questions before a link to the report will be emailed to you',
            'Please indicate product (tariff code) you are interested in exporting.',
          ],
          completed: true,
          value: null,
        },
        backup_query_result: {},
        backup_user_data: {},

        query_result: {
          status: null,
          message: '',
          response_url: '',
          input_data: {},
          result: {
            markets: 0,
            potential: null,
            market_count: null,
            chart_data: null,
            all_markets: [],
            plotly_chart: {
              data: {},
              layout: {},
            },
          },
        },
        report_result: null,
        user_data: {
          firstname: '',
          lastname: '',
          company: '',
          website: '',
          location: '',
          product_code: '',
          market_preference: '',
          city: '',
          email: '',
          phone: '',
          mobile_phone: '',
          country_state: '',
          zipcode: '',
          //
          data_permission: false,
          required_1: false,
          //
          contact_permission: false,
          optional_1: false,
          //
          appointment_required: false,
          optional_2: false,
        },
        rules_passed: false,

        required_rule: [
          value => !!value || 'Required.',
          value =>
            (value && value.length >= FIRSTNAME_LENGTH) ||
            'Min ' + FIRSTNAME_LENGTH + ' characters',
        ],
        phonerequired_rule: [
          value => !!value || 'Required.',
          value =>
            (value && value.length >= PHONE_LENGTH) ||
            'Min ' + PHONE_LENGTH + ' characters [0..9, (), .]',
        ],
        state_required_rule: [
          value => !!value || 'Required.',
          value =>
            (value && value.length >= STATE_LENGTH) ||
            'Min ' + STATE_LENGTH + ' characters',
        ],
        zip_required_rule: [
          value => !!value || 'Required.',
          value =>
            (value && value.length >= ZIP_LENGTH) || 'Min ' + ZIP_LENGTH + ' characters',
        ],

        website_rule: [
          value => !!value || 'Required.',
          v =>
            /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/.test(v) ||
            'Website must be valid',
        ],
        email_rule: [
          value => !!value || 'Required.',
          v =>
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
            'E-mail must be valid',
        ],
        data_rule: [value => value === true || 'Required'],
        optional_rule: [value => value === true || 'Required'],
      };
    },
    watch: {},
    computed: {
      generate_chart_html() {
        if (!this.query_result['result']['plotly_chart']) {
          return {data: [], layout: {}};
        }
        let map = JSON.parse(this.query_result['result']['plotly_chart']);
        return {
          data: map.data,
          layout: map.layout,
        };
      },
      firstname_rules() {
        return this.user_data.firstname.length < FIRSTNAME_LENGTH;
      },
      lastname_rules() {
        return this.user_data.lastname.length < LASTNAME_LENGTH;
      },
      company_name_rules() {
        return this.user_data.company.length < COMPANY_NAME_LENGTH;
      },
      website_rules() {
        let v = this.user_data.website;
        return !/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/.test(v);
      },
      location_rules() {
        return this.user_data.location.length < LOCATION_LENGTH;
      },
      city_rules() {
        return this.user_data.city.length < CITY_LENGTH;
      },
      email_rules() {
        let v = this.user_data.email;
        return !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v);
      },
      phone_rules() {
        return (
          this.user_data.phone.length < PHONE_LENGTH ||
          !/^[0-9\s\-()+]+$/.test(this.user_data.phone)
        );
      },
      mobilephone_rules() {
        return (
          this.user_data.mobile_phone.length < MOBILE_PHONE_LENGTH ||
          !/^[0-9\s\-()+]+$/.test(this.user_data.mobile_phone)
        );
      },

      state_rules() {
        return this.user_data.country_state.length < STATE_LENGTH;
      },
      zip_rules() {
        return this.user_data.zipcode.length < ZIP_LENGTH;
      },

      map_settings() {
        return {
          packages: ['geochart'],
          mapsApiKey: this.maps_key,
          tooltip: {isHtml: true},
        };
      },
      get_app_pos() {
        return this.insitution_templates.STYLES.style['application_position'];
      },
      input_form_validated() {
        return (
          this.firstname_rules ||
          this.lastname_rules ||
          this.company_name_rules ||
          // // this.website_rules ||
          // this.location_rules ||
          // // this.city_rules ||
          this.email_rules ||
          this.phone_rules ||
          // this.mobilephone_rules ||
          // this.state_rules ||
          this.zip_rules ||
          !this.user_data.required_1 ||
          !this.box_3_next_clicked
        );
      },
      box_2_next_enabled() {
        // console.log(this.box_2_next_clicked);
        // console.log(this.export_already_radio);
        // box_2_next_clicked ||
        //              !export_already_radio === '0' ||
        //              !(export_already_radio && selected_current_markets.length > 0)

        if (this.box_2_next_clicked) {
          return true;
        }
        if (this.export_already_radio === '0') {
          return false;
        } else if (this.export_already_radio === '1') {
          if (this.selected_current_markets.length > 0) {
            return false;
          } else return true;
        } else return true;
      },
      chart_data() {
        let data = this.query_result['result']['chart_data'];
        let return_data = [this.query_result['result']['chart_data'][0]];
        // let return_data = [['Country', , 'Potential']];
        let max_value = 0;
        for (let i = 1; i < data.length; i++) {
          let entry = data[i];
          let v0 = entry[0];

          let v1 = entry[1];
          if (v1 > max_value) {
            max_value = v1;
          }

          v1 = (v1 / 1000) * this.institution['excange_rate'];
          v1 =
            Math.round(v1 * this.institution['decimals']) / this.institution['decimals'];

          return_data.push([v0, v1]);
        }
        console.log(max_value);
        return_data.push([this.institution['C_Name'], max_value + 100]);
        return return_data || [];
      },

      chart_local_options() {
        console.log(this.chart_values());
        console.log(this.chart_colours());
        console.log(this.get_max);
        return {
          region: 'world',
          legend: 'none',
          displayMode: 'markers', //region
          colorAxis: {
            values: this.chart_values(),
            colors: this.chart_colours(),
          },

          backgroundColor: {
            strokeWidth: '0' /* control the countries borders width */,
            stroke: 'lightgray',
          },
          datalessRegionColor: 'lightgray',
          resolution: 'countries',
          height: 360,
          forceFrame: true,
          enableRegionInteractivity: false,
          magnifyingGlass: {enable: true, zoomFactor: 7.5},
          tooltip: {
            isHtml: false,
          },
        };
      },
      potential_calc() {
        const decimals = Number(this.institution.decimals || 100);
        const monetaryValue =
          Number(this.query_result.result.potential || 0) *
          Number(this.institution.excange_rate || 1) *
          1000;
        let divisor = 1;
        let magnitude = '';
        if (monetaryValue >= 1000000000) {
          divisor = 1000000000;
          magnitude = 'billion';
        } else if (monetaryValue >= 1000000) {
          divisor = 1000000;
          magnitude = 'million';
        }
        const scaled = Math.round((monetaryValue / divisor) * decimals) / decimals;
        return `${scaled.toLocaleString()} ${magnitude} `;
      },
      box_1_active() {
        return (
          this.hs6_selected == null || this.query_result['result']['market_count'] === 0
        );
      },
      box_2_active() {
        return (
          this.hs6_selected != null &&
          this.query_result['result']['market_count'] > 0 &&
          !this.box_2_next_clicked
        );
      },
      box_3_active() {
        return this.box_2_next_clicked && !this.box_3_next_clicked;
      },
      contact_active() {
        return this.box_3_next_clicked;
      },
      stylef() {
        return this.insitution_templates.STYLES.style;
      },
      main_color() {
        return this.insitution_templates.STYLES.style['main_color'];
      },
      result_map_box_text() {
        return (
          'Our analysis indicates ' +
          this.query_result.result.market_count +
          " markets with 'untapped' potential estimated at " +
          this.potential_calc +
          this.institution['currency_str_display_name'] +
          '.'
        );
      },
      chapter_items_list() {
        let return_list = [];
        Object.keys(this.chapters)
          .sort()
          .forEach(key => {
            return_list.push({
              text: key,
              value: key,
            });
          });
        return return_list.length > 0 ? return_list : 'Loading ...';
      },
      hs2_items_list() {
        let return_list = [];
        Object.keys(this.hs2_codes)
          .sort()
          .forEach(key => {
            return_list.push({
              text: key + ' - ' + this.hs2_codes[key],
              value: key,
            });
          });
        return return_list;
      },
      hs4_items_list() {
        let return_list = [];
        Object.keys(this.hs4_codes)
          .sort()
          .forEach(key => {
            return_list.push({
              text: key + ' - ' + this.hs4_codes[key],
              value: key,
            });
          });
        return return_list;
      },
      hs6_items_list() {
        let return_list = [];
        Object.keys(this.hs6_codes)
          .sort()
          .forEach(key => {
            return_list.push({
              text: key + ' - ' + this.hs6_codes[key],
              value: key,
            });
          });
        return return_list;
      },
      all_hs6_items_list() {
        let return_list = [];
        Object.keys(this.all_hs6_codes)
          .sort()
          .forEach(key => {
            return_list.push({
              text: key + ' - ' + this.all_hs6_codes[key],
              value: key,
            });
          });
        return return_list;
      },
      market_results_dropdown_data() {
        if (this.query_result) {
          return Object.keys(this.query_result.result.markets).sort();
        } else return [];
      },
      institutionApiUrl() {
        return `${this.emf_lead_gen_url}/v2/institutions/${this.institution_uuid}`;
      },
      sanitized_header() {
        return this.renderInstitutionFragment(this.insitution_templates.HEADER);
      },
      sanitized_footer() {
        return this.renderInstitutionFragment(this.insitution_templates.FOOTER);
      },
      sanitized_disclaimer() {
        return DOMPurify.sanitize(
          (this.institution && this.institution.frond_end_disclaimer) || 'Loading...',
        );
      },

      entry_box_style() {
        if (this.insitution_templates.STYLES) {
          return this.insitution_templates.STYLES['style']['entry_box'];
        } else return null;
      },
      drop_down_style() {
        if (this.insitution_templates.STYLES) {
          return this.insitution_templates.STYLES['style']['drop_down'];
        } else return null;
      },
      drop_down_props() {
        if (this.insitution_templates.STYLES) {
          return this.insitution_templates.STYLES['style']['drop_down_props'];
        } else return null;
      },
    },
    methods: {
      /** Render allowlisted institution asset placeholders before sanitizing HTML. */
      renderInstitutionFragment(fragment) {
        const assetUrl = name => {
          const relativeUrl = this.institution && this.institution[name];
          return relativeUrl ? this.emf_lead_gen_url + relativeUrl : '';
        };
        const replacements = {
          logo_url: assetUrl('logo_url'),
          flag_url: assetUrl('flag_url'),
        };
        const rendered = String(fragment || '').replace(
          /\{\{\s*(logo_url|flag_url)\s*\}\}/g,
          (placeholder, name) => replacements[name] || '',
        );
        return DOMPurify.sanitize(rendered);
      },
      /** Install validated institution fragment CSS and local font assets. */
      installInstitutionStyles(styles) {
        const oldStyle = document.getElementById('institution-fragment-style');
        if (oldStyle) oldStyle.remove();
        if (!styles || typeof styles.fragment_css !== 'string') return;

        const fontFaces = Array.isArray(styles.font_assets)
          ? styles.font_assets
              .filter(
                font =>
                  font &&
                  /^[A-Za-z0-9 ]+$/.test(font.family || '') &&
                  /^[a-z0-9-]+$/.test(font.asset || '') &&
                  Number.isInteger(font.weight),
              )
              .map(
                font =>
                  `@font-face{font-family:"${font.family}";font-style:normal;` +
                  `font-weight:${font.weight};src:url("${window.location.origin}/v2/institutions/` +
                  `${this.institution_uuid}/assets/${font.asset}") ` +
                  `format("truetype");}`,
              )
              .join('')
          : '';
        const style = document.createElement('style');
        style.id = 'institution-fragment-style';
        style.textContent = fontFaces + styles.fragment_css;
        document.head.appendChild(style);
      },
      previous_clicked() {
        this.box_2_next_clicked = true;
        this.show_success_page = false;
        this.loading = false;
      },
      chart_values() {
        let output = _.clone(this.query_result['result']['chart_data'].map(v => v[1]));
        output.splice(0, 1);
        let sorted_list = output.sort((a, b) => a - b);
        sorted_list.push(sorted_list[sorted_list.length - 1] + 100);

        return sorted_list;
      },
      chart_colours() {
        let colors_array = Array(
          this.query_result['result']['chart_data'].map(v => v[0]).length - 1,
        ).fill('green');
        colors_array.push('#00cccc');
        return colors_array;
      },

      reset_form_data() {
        this.user_data = _.clone(this.backup_user_data);
      },
      box_3_previous_button() {
        this.box_2_next_clicked = false;
        this.spesific_selected_market = '';
      },
      /** Reactivate product selection before returning to section 1. @returns {void} */
      box_2_previous_button() {
        this.hs6_selected = '';
        this.query_result['result']['market_count'] = 0;
        this.query_result['result']['all_markets'] = [];
        this.export_already_radio = false;
        this.selected_current_markets = [];
      },
      clear_box_1() {
        this.hs6_selected = '';
        this.hs4_selected = '';
        this.hs2_selected = '';
        this.chapter_selected = '';
        // this.chapters = {};
        this.hs2_codes = {};
        this.hs4_codes = {};
        this.hs6_codes = {};
        this.box_2_next_clicked = false;
        this.box_3_next_clicked = false;
        //
        this.user_data.data_permission = false;
        this.user_data.required_1 = false;
        //
        this.user_data.contact_permission = false;
        this.user_data.optional_1 = false;
        //
        this.user_data.appointment_required = false;
        this.user_data.optional_2 = false;

        //
        this.customer_note = '';
        this.query_result.result = {
          markets: 0,
          potential: null,
          market_count: null,
          chart_data: null,
          all_markets: [],
        };
      },

      form_previous_clicked() {
        this.box_3_next_clicked = false;
        const element = document.getElementById('box_1');
        element.scrollIntoView();
      },
      start_over_clicked() {
        this.show_success_page = false;
        this.appointment_requested = false;
        const element = document.getElementById('box_1');
        element.scrollIntoView();
        this.clear_box_1();
        this.clear_inputs();
      },
      /** Advance to contact details after a target market is selected. */
      box_3_clicked(event) {
        this.spesific_selected_market = event || '';
        if (!this.spesific_selected_market) return;
        // this.take_map_images();
        this.box_3_next_clicked = true;
        const element = document.getElementById('contact_form');
        element.scrollIntoView();
      },
      /** Store the final HS6 selection and load its market combinations. */
      set_hs6_selection(event) {
        this.hs6_selected = event || '';
        if (!this.hs6_selected) return;

        this.hs2_selected = this.hs6_selected.substring(0, 2);
        this.hs4_selected = this.hs6_selected.substring(0, 4);

        // Store the current HS6 codes to preserve them
        const currentHS6Codes = {...this.hs6_codes};

        this.chapter_selected = this.hs2_to_chapter_map[this.hs2_selected];
        this.load_hs2_codes(this.chapter_selected);
        this.load_hs4_codes(this.hs2_selected);

        this.hs6_codes = currentHS6Codes;
        this.get_crm_lead_generate_combintaions();
      },
      loadDynamicStyles(styleFile) {
        const head = document.getElementsByTagName('head')[0];
        const oldStyle = document.getElementById('dynamic-style');

        if (oldStyle) {
          head.removeChild(oldStyle);
        }

        const link = document.createElement('link');
        link.id = 'dynamic-style';
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = styleFile;

        head.appendChild(link);
      },
      clear_inputs() {
        this.selected_current_markets = [];
        this.spesific_selected_market = '';
        this.export_already_radio = false;
      },
      async get_image_from_canvas(html_id) {
        await window.scroll(0, 0);

        let element = document.getElementById(html_id);

        await html2canvas(element, {
          useCORS: true,
          allowTaint: false,
          letterRendering: true,
          profile: true,
        })
          .then(canvas => {
            const image_uri = canvas.toDataURL('image/png');
            let return_uri = image_uri.replace(/^data:image\/(png|jpg);base64,/, '');
            if (html_id === 'top6_map') {
              this.top6_map_b64 = return_uri;
            } else {
              this.products_map_b64 = return_uri;
            }
          })
          .catch(() => {
            this.products_map_b64 = 'Error';
            this.top6_map_b64 = 'Error';
          });
      },

      load_chapter_data() {
        // clicked_level = 0 will request a dictionary of the chapter values
        return axios
          .post(this.institutionApiUrl + '/products', {
            clicked_level: 0,
            clicked_code: '',
          })
          .then(response => {
            this.chapters = response.data;
            this.chapters_loading = false;
          })
          .catch(() => {
            this.chapters_loading = false;
            this.product_load_error = 'Product groups could not be loaded.';
          });
      },
      load_hs2_codes(event) {
        this.chapter_selected = event;
        this.hs2_loading = true;
        this.hs2_codes = {};
        this.hs4_codes = {};
        this.hs6_codes = {};
        return axios
          .post(this.institutionApiUrl + '/products', {
            clicked_level: 1,
            clicked_code: this.chapter_selected,
          })
          .then(response => {
            this.hs2_codes = response.data;
            this.hs2_loading = false;
          })
          .catch(() => {
            this.hs2_loading = false;
            this.product_load_error = 'Product headings could not be loaded.';
          });
      },
      load_hs4_codes(event) {
        this.hs2_selected = event;
        this.hs4_loading = true;
        this.hs4_codes = {};
        this.hs6_codes = {};
        return axios
          .post(this.institutionApiUrl + '/products', {
            clicked_level: 2,
            clicked_code: this.hs2_selected,
          })
          .then(response => {
            this.hs4_codes = response.data;
            this.hs4_loading = false;
          })
          .catch(() => {
            this.hs4_loading = false;
            this.product_load_error = 'Product sub-headings could not be loaded.';
          });
      },
      load_hs6_codes(event) {
        this.hs4_selected = event;
        this.hs6_loading = true;
        return axios
          .post(this.institutionApiUrl + '/products', {
            clicked_level: 3,
            clicked_code: this.hs4_selected,
          })
          .then(response => {
            this.hs6_codes = response.data;
            this.hs6_loading = false;
          })
          .catch(() => {
            this.hs6_loading = false;
            this.product_load_error = 'Specific products could not be loaded.';
          });
      },
      load_all_hs6_codes() {
        // this.hs4_selected = event;
        this.hs6_loading = true;
        return axios
          .post(this.institutionApiUrl + '/products', {
            clicked_level: 4,
            clicked_code: '',
          })
          .then(response => {
            this.all_hs6_codes = response.data;
            this.hs6_loading = false;
          })
          .catch(() => {
            this.hs6_loading = false;
            this.product_load_error = 'The product list could not be loaded.';
          });
      },
      map_hs2_to_chapter_codes() {
        // this.hs4_selected = event;

        return axios
          .post(this.institutionApiUrl + '/products', {
            clicked_level: 5,
            clicked_code: '',
          })
          .then(response => {
            this.hs2_to_chapter_map = response.data;
            // this.hs6_loading = false;
          })
          .catch(() => {
            this.product_load_error = 'The product hierarchy could not be loaded.';
          });
      },

      load_institution_data() {
        log('Loading templates for institution: ' + this.institution_uuid);
        return axios
          .get(this.institutionApiUrl + '/public-settings')
          .then(response => {
            const institution_data = response.data;
            this.insitution_templates.STYLES = institution_data.styles;
            this.installInstitutionStyles(institution_data.styles);
            this.insitution_templates.HEADER = institution_data.header;
            this.insitution_templates.FOOTER = institution_data.footer;
            institution_data.frond_end_disclaimer =
              institution_data.frontend_disclaimer_html;
            institution_data.excange_rate = institution_data.exchange_rate;
            institution_data.flag_image_b64 = institution_data.flag_url
              ? this.emf_lead_gen_url + institution_data.flag_url
              : '';
            this.institution = institution_data;
            document.title = `${institution_data.brand_display_name} | Export Market Finder`;
            if (institution_data.logo_url) {
              const favicon = document.querySelector('link[rel="icon"]');
              if (favicon)
                favicon.href = this.emf_lead_gen_url + institution_data.logo_url;
            }
            this.product_box.text = this.dummy_data.dummy_product_box_text;
          })
          .catch(() => {
            this.institution_load_error =
              'This institution link is invalid, inactive, or unavailable.';
          });
      },
      temp_auto_fill_and_submit() {
        this.user_data = _.clone(this.dummy_data.dummy_user);
        this.submit_request();
      },
      get_crm_lead_generate_combintaions() {
        this.combinations_loading = true;
        this.combination_error = '';
        let query_data = {
          selected_product: this.hs6_selected,
        };

        axios
          .post(this.institutionApiUrl + '/combinations', query_data)
          .then(response => {
            this.query_result = response.data;
            this.combinations_loading = false;
            if (response.data.result.market_count === 0) {
              this.combination_error =
                'No export-potential markets are available for this product.';
            }
          })
          .catch(() => {
            this.combinations_loading = false;
            this.combination_error = 'Trade model data is currently unavailable.';
          });
      },

      reset() {
        this.query_result = _.clone(this.backup_query_result);
        this.user_data = _.clone(this.backup_user_data);
        this.chapter_selected = '';
        this.hs2_selected = '';
        this.hs2_items_list = '';
        this.hs4_selected = '';
        this.hs6_selected = '';
      },
      // take_map_images() {
      //   this.get_image_bas64('product_map');
      //   this.get_image_bas64('top6_map');
      // },
      //
      async submit_request() {
        this.loading = true;
        this.user_data.product_code = this.hs6_selected;
        this.user_data.market_preference = this.spesific_selected_market;
        this.user_data.email = this.user_data.email.toLowerCase();
        let data = _.clone(this.user_data);
        data['data_permission'] = data.required_1;
        data['contact_permission'] = data.optional_1;
        data['appointment_required'] = data.optional_2;
        data['chapter'] = this.chapter_selected || '';
        data['hs2_code'] = this.hs2_selected || '';
        data['hs4_code'] = this.hs4_selected || '';
        data['hs6_code'] = this.hs6_selected;
        data['hs6_code_description'] =
          this.all_hs6_codes[this.hs6_selected] || this.hs6_codes[this.hs6_selected];
        if (!data['hs6_code_description']) {
          this.loading = false;
          alert(
            'The selected product description is unavailable. Please select it again.',
          );
          return;
        }
        data['exporting_to_other_countries'] = this.export_already_radio === '1';
        data['exporting_to_countries'] = this.selected_current_markets.toString();
        data['spesific_market_for_report'] = this.spesific_selected_market;

        data['products_map_b64'] = this.products_map_b64;
        data['top6_map_b64'] =
          this.top6_map_b64 && this.top6_map_b64 !== '-' ? this.top6_map_b64 : null;
        data['customer_note'] = this.customer_note.substring(0, 500);

        let url = this.institutionApiUrl + '/submissions';

        axios
          .post(url, data)
          .then(response => {
            this.report_result = response.data;
            console.log(this.report_result['status']);

            if (this.report_result['status'] === 'OK') {
              this.appointment_requested = data.optional_2 === true;
              this.show_success_page = true;
              this.loading = false;
              this.clear_inputs();
            } else if (this.report_result['status'] === 'EXCEEDED') {
              this.loading = false;
              this.show_success_page = false;
              alert('The report request limit has been reached.');
            } else if (this.report_result['status'] === 'DOMAIN BLACKLISTED') {
              this.loading = false;
              this.show_success_page = false;
              this.show_app = false;
            }
          })
          .catch(e => {
            const detail = e.response && e.response.data && e.response.data.detail;
            alert(detail || 'The request could not be submitted. Please try again.');
            this.loading = false;
            this.show_success_page = false;
          });
      },

      rules_valid() {
        this.rules_passed = this.$refs.user_input_form.validate();
        return this.rules_passed;
      },
    },

    //
  };
</script>

<style scoped>
  .institution-dark {
    color: #fff8f2;
  }

  .institution-dark a {
    color: #f0c9a5 !important;
  }

  .institution-dark ::v-deep .theme--light.v-input,
  .institution-dark ::v-deep .theme--light.v-input input,
  .institution-dark ::v-deep .theme--light.v-input textarea,
  .institution-dark ::v-deep .theme--light.v-input .v-label,
  .institution-dark ::v-deep .theme--light.v-input .v-icon,
  .institution-dark ::v-deep .theme--light.v-input .v-messages__message,
  .institution-dark ::v-deep .theme--light.v-input .v-select__selection {
    color: #fff8f2 !important;
  }

  .institution-dark ::v-deep .theme--light.v-input .v-input__slot::before {
    border-color: rgba(255, 248, 242, 0.65) !important;
  }

  .institution-dark ::v-deep .theme--light.v-textarea textarea,
  .institution-dark ::v-deep .theme--light.v-textarea .v-label,
  .institution-dark ::v-deep .theme--light.v-textarea .v-icon,
  .institution-dark ::v-deep .theme--light.v-textarea .v-counter {
    color: #3b241a !important;
  }

  .institution-dark ::v-deep .v-btn--disabled {
    background-color: #6f4a32 !important;
    color: rgba(255, 248, 242, 0.75) !important;
  }

  .institution-dark ::v-deep .v-btn--disabled .v-icon {
    color: rgba(255, 248, 242, 0.75) !important;
  }

  .institution-dark .red--text {
    color: #ffb4ab !important;
  }

  ::v-deep .theme--light.v-list-item .v-list-item__mask {
    background-color: red !important;
    color: white;
    padding: 2px;
    margin: 2px;
  }

  .chartWithOverlay {
    position: relative;
    background: white;
  }

  .green_dot {
    height: 10px;
    width: 10px;
    background-color: darkgreen;
    border-radius: 50%;
    display: inline-block;
    margin-right: 5px;
  }

  .home_dot {
    height: 10px;
    width: 10px;
    background-color: #0f13f7;
    border-radius: 50%;
    display: inline-block;
    margin-right: 5px;
  }

  .google-visualization-tooltip-item:first-child {
    display: none;
    visibility: hidden;
  }

  .google-visualization-tooltip-item span {
    font-weight: bold;
  }
</style>
